import React, { useCallback, useEffect, useState } from "react";
import {
  ActiveIvrCall,
  clearActiveIvrCalls,
  fetchTeamActiveCalls,
  refreshActiveIvrCalls,
  useActiveIvrCalls,
} from "../../../../utils/useActiveIvrCalls";
import history from "../../../../utils/history";
import urls from "../../../../global/constants/UrlConstants";
import { getCallParams, makeCall } from "../../../../utils/service";
import { store } from "../../../../utils/store";

const statusLabel: Record<string, string> = {
  initiated: "Connecting…",
  dialing: "Dialing…",
  ringing: "Ringing…",
  answered: "On call (answered)",
};

const statusClass: Record<string, string> = {
  initiated: "bg-slate-100 text-slate-700",
  dialing: "bg-amber-50 text-amber-700",
  ringing: "bg-amber-50 text-amber-700",
  answered: "bg-emerald-50 text-emerald-700",
};

export default function LiveIvrCallsPanel() {
  const { calls: myCalls, reload } = useActiveIvrCalls();
  const isAdmin = String(store.getState()?.auth?.hierarchyLevel || "") === "1";
  const [teamCalls, setTeamCalls] = useState<ActiveIvrCall[]>([]);
  const [endingId, setEndingId] = useState<string | null>(null);

  const loadTeam = useCallback(async () => {
    if (!isAdmin) {
      setTeamCalls([]);
      return;
    }
    try {
      const rows = await fetchTeamActiveCalls();
      setTeamCalls(rows);
    } catch {
      setTeamCalls([]);
    }
  }, [isAdmin]);

  useEffect(() => {
    // One check on mount; poll only while there are live team calls
    loadTeam();
  }, [loadTeam]);

  useEffect(() => {
    if (!isAdmin || teamCalls.length === 0) return;
    const id = setInterval(loadTeam, 5000);
    return () => clearInterval(id);
  }, [isAdmin, teamCalls.length, loadTeam]);

  // Admin sees org-wide live calls; team members see only their own
  const calls = isAdmin ? teamCalls : myCalls;

  if (!calls.length) return null;

  const endCall = async (call: {
    _id: string;
    uniqueCallId?: string;
    referenceId?: string;
  }) => {
    if (endingId) return;
    setEndingId(call._id);
    try {
      const params = await getCallParams("POST", {
        activeCallId: call._id,
        uniqueCallId: call.uniqueCallId,
        referenceId: call.referenceId,
        endAllForAgent: false,
      });
      await makeCall(urls.ACTIVECALLSEND, params);
      clearActiveIvrCalls({ refetch: false });
      await refreshActiveIvrCalls();
      await loadTeam();
    } catch (_) {
      /* ignore */
    } finally {
      setEndingId(null);
    }
  };

  const endAll = async () => {
    if (endingId) return;
    setEndingId("all");
    try {
      const params = await getCallParams("POST", { endAllForAgent: true });
      await makeCall(urls.ACTIVECALLSEND, params);
      clearActiveIvrCalls({ refetch: false });
      await refreshActiveIvrCalls();
      await loadTeam();
    } catch (_) {
      /* ignore */
    } finally {
      setEndingId(null);
    }
  };

  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-[#F3D4DE] bg-[#FFF8FA]">
      <div className="flex items-center justify-between border-b border-[#F3D4DE] px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#841C39] opacity-60" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#841C39]" />
          </span>
          <h3 className="text-sm font-semibold text-[#841C39]">
            Live IVR calls ({calls.length})
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={endAll}
            disabled={!!endingId}
            className="text-xs font-semibold text-[#841C39] hover:underline disabled:opacity-50"
          >
            End all
          </button>
          <button
            type="button"
            onClick={() => {
              reload();
              loadTeam();
            }}
            className="text-xs font-medium text-[#6B7280] hover:text-[#841C39]"
          >
            Refresh
          </button>
        </div>
      </div>
      <ul className="divide-y divide-[#F3D4DE]">
        {calls.map((call) => (
          <li
            key={call._id}
            className="flex items-center justify-between gap-3 px-4 py-3"
          >
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              onClick={() => {
                if (call.leadId) {
                  history.push(
                    urls.ALLLEADDETAILS.replace(":id", String(call.leadId))
                  );
                }
              }}
            >
              <p className="truncate text-sm font-semibold text-[#111827]">
                {call.agentName || "Agent"}
                <span className="font-normal text-[#9CA3AF]"> → </span>
                {call.leadName || call.customerNumber || "Customer"}
              </p>
              <p className="mt-0.5 text-xs text-[#6B7280]">
                <span
                  className={`mr-1.5 inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                    statusClass[call.status] || statusClass.initiated
                  }`}
                >
                  {statusLabel[call.status] || call.status}
                </span>
                <span className="font-mono font-semibold tabular-nums text-[#841C39]">
                  {call.durationFormatted || "0:00"}
                </span>
              </p>
            </button>
            <button
              type="button"
              onClick={() => endCall(call)}
              disabled={!!endingId}
              className="shrink-0 rounded-lg border border-[#E8D0D8] bg-white px-2.5 py-1 text-[11px] font-semibold text-[#841C39] hover:bg-[#FCEEF3] disabled:opacity-50"
            >
              {endingId === call._id ? "…" : "End"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
